import type { CatalogItem, Chip, Digest, Peripheral, ProvenanceItem, Register } from "./types";

export const DEMO_RELEASE = `sha256:${"8fd3c9a1".repeat(8)}` as Digest;
const uartRevision = `sha256:${"27a4e5bc".repeat(8)}` as Digest;
export const demoRegister: Register = {
  entity_id: "10000000-0000-4000-8000-000000000014",
  revision_id: uartRevision,
  peripheral_entity_id: "10000000-0000-4000-8000-000000000001",
  peripheral_name: "LPUART",
  name: "STAT",
  description: "Status flags and receive error conditions.",
  offset: "0x14",
  width: 32,
  reset_value: "0xc0000000",
  access: { readable: true, writable: true },
  fields: [
    {
      name: "TDRE",
      description: "Transmit data register empty",
      lsb: 23,
      width: 1,
      access: { readable: true, writable: false },
    },
    {
      name: "RDRF",
      description: "Receive data register full",
      lsb: 21,
      width: 1,
      access: { readable: true, writable: false },
    },
    {
      name: "OR",
      description: "Receiver overrun",
      lsb: 19,
      width: 1,
      access: { readable: true, writable: true, write_action: "one-to-clear" },
    },
  ],
};
export const demoPeripheral: Peripheral = {
  entity_id: "10000000-0000-4000-8000-000000000001",
  revision_id: uartRevision,
  name: "LPUART",
  description: "Low-power universal asynchronous receiver/transmitter with DMA and wake support.",
  vendor_revision: "v4.1",
  registers: [
    {
      ...demoRegister,
      entity_id: "10000000-0000-4000-8000-000000000010",
      name: "BAUD",
      offset: "0x10",
      description: "Baud rate and sampling configuration.",
      fields: [],
    },
    demoRegister,
    {
      ...demoRegister,
      entity_id: "10000000-0000-4000-8000-000000000018",
      name: "CTRL",
      offset: "0x18",
      description: "Transmitter and receiver control.",
      fields: [],
    },
  ],
  used_by: [
    {
      chip_entity_id: "20000000-0000-4000-8000-000000000093",
      chip_name: "i.MX93",
      instance_name: "LPUART1",
    },
  ],
};
export const demoChip: Chip = {
  entity_id: "20000000-0000-4000-8000-000000000093",
  revision_id: `sha256:${"a4d09e62".repeat(8)}` as Digest,
  name: "i.MX93",
  aliases: ["MIMX9352", "iMX93"],
  vendor: "NXP",
  family: "i.MX 9",
  architectures: ["Armv8-A", "Armv8-M"],
  address_spaces: ["a55", "m33"],
  memory_regions: [
    { name: "OCRAM", address_space: "m33", base: "0x20480000", size: "0x40000", kind: "sram" },
    { name: "TCM_CODE", address_space: "m33", base: "0x0ffe0000", size: "0x20000", kind: "tcm" },
    { name: "DDR", address_space: "a55", base: "0x80000000", size: "0x80000000", kind: "dram" },
  ],
  peripheral_instances: [
    {
      name: "LPUART1",
      peripheral: demoPeripheral.entity_id,
      peripheral_revision: uartRevision,
      peripheral_name: "LPUART",
      mappings: [{ address_space: "m33", base: "0x44380000", size: "0x10000" }],
    },
    {
      name: "LPUART2",
      peripheral: demoPeripheral.entity_id,
      peripheral_revision: uartRevision,
      peripheral_name: "LPUART",
      mappings: [{ address_space: "a55", base: "0x44390000", size: "0x10000" }],
    },
  ],
  interrupts: [{ source: "LPUART1", execution_context: "m33", controller: "NVIC", irq_number: 52 }],
};
export const demoCatalog: CatalogItem[] = [
  {
    row_id: 1,
    kind: "chip",
    entity_id: demoChip.entity_id,
    revision_id: demoChip.revision_id,
    name: "i.MX93",
    subtitle: "NXP · i.MX 9 · Armv8-A / Armv8-M",
    description:
      "Heterogeneous application processor with Cortex-A55 and Cortex-M33 execution contexts.",
  },
  {
    row_id: 2,
    kind: "peripheral",
    entity_id: demoPeripheral.entity_id,
    revision_id: demoPeripheral.revision_id,
    name: "LPUART",
    subtitle: "Peripheral IP · revision v4.1",
    description: demoPeripheral.description,
  },
  {
    row_id: 3,
    kind: "register",
    entity_id: demoRegister.entity_id,
    revision_id: demoRegister.revision_id,
    name: "STAT",
    subtitle: "LPUART · offset 0x14 · 32 bits",
    description: demoRegister.description,
  },
];
export const demoProvenance: ProvenanceItem[] = [
  {
    target_path: "/registers/1/offset",
    source_revision: `sha256:${"b11d09f3".repeat(8)}` as Digest,
    locator_kind: "cmsis-symbol",
    locator: "LPUART_STAT",
    extraction_method: "cmsis-header-parser",
    confidence: "exact",
  },
  {
    target_path: "/registers/1/fields/2/write_action",
    source_revision: `sha256:${"c73a8021".repeat(8)}` as Digest,
    locator_kind: "svd-xpath",
    locator: "/device/peripherals/peripheral/registers/register[name='STAT']",
    extraction_method: "cmsis-svd-importer",
    confidence: "derived",
  },
];
